const SIGNAL_WEIGHT = {
  view: 1,
  story: 1.5,
  search: 2,
  like: 4,
  bookmark: 5,
  follow: 7,
  unlock: 8,
  subscribe: 10,
  chat: 6
};

const normal = (value = '') => String(value).trim().toLocaleLowerCase('de');
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export function emptyBehaviorProfile() {
  return { topics: {}, creators: {}, searches: {}, seenPosts: {}, updatedAt: 0 };
}

export function sanitizeBehaviorProfile(value) {
  const cleanMap = (input, limit = 80) => Object.fromEntries(Object.entries(input || {})
    .filter(([key, score]) => key && Number.isFinite(Number(score)))
    .slice(-limit)
    .map(([key, score]) => [normal(key), clamp(Number(score), -20, 100)]));
  return {
    topics: cleanMap(value?.topics),
    creators: cleanMap(value?.creators),
    searches: cleanMap(value?.searches, 30),
    seenPosts: cleanMap(value?.seenPosts, 150),
    updatedAt: Number(value?.updatedAt) || 0
  };
}

export function recordRecommendationSignal(profile, signal) {
  const next = sanitizeBehaviorProfile(profile);
  const weight = SIGNAL_WEIGHT[signal.type] || 1;
  for (const topic of signal.topics || []) {
    const key = normal(topic);
    if (key) next.topics[key] = clamp((next.topics[key] || 0) + weight, -20, 100);
  }
  const creator = normal(signal.creatorId);
  if (creator) next.creators[creator] = clamp((next.creators[creator] || 0) + weight, -20, 100);
  const query = normal(signal.query);
  if (query.length >= 3) next.searches[query] = clamp((next.searches[query] || 0) + weight, -20, 40);
  const postId = normal(signal.postId);
  if (postId && ['view', 'unlock'].includes(signal.type)) next.seenPosts[postId] = (next.seenPosts[postId] || 0) + 1;
  next.updatedAt = Date.now();
  return next;
}

export function buildTasteProfile({ me = {}, posts = [], creators = [], behavior = {} }) {
  const safe = sanitizeBehaviorProfile(behavior);
  const topics = { ...safe.topics };
  const creatorScores = { ...safe.creators };
  const addTopic = (topic, amount) => {
    const key = normal(topic);
    if (key) topics[key] = (topics[key] || 0) + amount;
  };
  const addCreator = (id, amount) => {
    const key = normal(id);
    if (key) creatorScores[key] = (creatorScores[key] || 0) + amount;
  };

  for (const interest of me.interests || []) addTopic(interest, 6);
  for (const creator of creators) {
    if (creator.following || creator.followStatus === 'accepted') addCreator(creator.id, 8);
    if (creator.subscribed) addCreator(creator.id, 12);
  }
  for (const post of posts) {
    const topicWeight = post.bookmarked ? 6 : post.liked ? 4 : post.unlocked && post.accessMode !== 'free' ? 3 : 0;
    if (topicWeight) for (const topic of post.topics || []) addTopic(topic, topicWeight);
    if (post.bookmarked || post.liked) addCreator(post.author?.id, post.bookmarked ? 4 : 2);
  }
  return { topics, creators: creatorScores, searches: safe.searches, seenPosts: safe.seenPosts };
}

function freshnessScore(created) {
  const ageHours = Math.max(0, (Date.now() - new Date(created).getTime()) / 36e5);
  return Math.max(0, 8 - Math.log2(ageHours + 1) * 1.4);
}

function searchAffinity(values, searches) {
  const haystack = normal(values.filter(Boolean).join(' '));
  return Object.entries(searches || {}).reduce((sum, [query, weight]) => {
    const terms = normal(query).split(/\s+/).filter((term) => term.length >= 3);
    const matches = terms.filter((term) => haystack.includes(term)).length;
    return sum + (terms.length ? Number(weight) * (matches / terms.length) : 0);
  }, 0);
}

export function scorePost(post, profile) {
  const topicFit = (post.topics || []).reduce((sum, topic) => sum + (profile.topics[normal(topic)] || 0), 0);
  const creatorFit = profile.creators[normal(post.author?.id)] || 0;
  const quality = Math.log10(Math.max(1, (post.likes || 0) + (post.comments || 0) * 3)) * 2.2;
  const novelty = (profile.seenPosts[normal(post.id)] || 0) * 2.5;
  const accessible = post.unlocked ? 1.2 : 0;
  const searched = searchAffinity([post.text, post.author?.name, post.author?.username, ...(post.topics || [])], profile.searches);
  return topicFit * 1.6 + creatorFit * 1.9 + searched * 1.4 + freshnessScore(post.created) + quality + accessible - novelty;
}

export function rankPosts(posts, profile) {
  const ranked = posts.map((post, index) => ({ post, index, score: scorePost(post, profile) }))
    .sort((a, b) => b.score - a.score || a.index - b.index);
  const result = [];
  while (ranked.length) {
    const recentAuthors = result.slice(-2).map((entry) => entry.author?.id);
    const nextIndex = ranked.findIndex((entry) => !recentAuthors.includes(entry.post.author?.id));
    result.push(ranked.splice(nextIndex < 0 ? 0 : nextIndex, 1)[0].post);
  }
  return result;
}

export function scoreCreator(creator, profile) {
  const interests = (creator.interests || []).reduce((sum, topic) => sum + (profile.topics[normal(topic)] || 0), 0);
  const affinity = profile.creators[normal(creator.id)] || 0;
  const quality = Math.log10(Math.max(1, creator.followers || creator.subscribers || 1)) * 2;
  const searched = searchAffinity([creator.name, creator.username, creator.bio, ...(creator.interests || [])], profile.searches);
  return interests * 1.5 + affinity * 2 + searched * 1.4 + quality + (creator.active ? 2 : 0);
}

export function rankCreators(creators, profile) {
  return creators.map((creator, index) => ({ creator, index, score: scoreCreator(creator, profile) }))
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map(({ creator }) => creator);
}

export function rankStories(stories, creators, profile) {
  const creatorById = new Map(creators.map((creator) => [creator.id, creator]));
  return stories.map((story, index) => {
    const creator = creatorById.get(story.authorId);
    const score = (story.mine ? 1000 : 0) + (story.following ? 80 : 0) + (story.seen ? -15 : 5)
      + (profile.creators[normal(story.authorId)] || 0) * 2 + (creator ? scoreCreator(creator, profile) : 0);
    return { story, index, score };
  }).sort((a, b) => b.score - a.score || a.index - b.index).map(({ story }) => story);
}

export function suggestedTopics(profile, availableTopics, limit = 6) {
  return [...new Set(availableTopics)].sort((a, b) => (profile.topics[normal(b)] || 0) - (profile.topics[normal(a)] || 0)).slice(0, limit);
}

export function recommendationReason(post, profile) {
  const topic = [...(post.topics || [])].sort((a, b) => (profile.topics[normal(b)] || 0) - (profile.topics[normal(a)] || 0))[0];
  if (topic && (profile.topics[normal(topic)] || 0) > 0) return `Weil du #${topic} magst`;
  if ((profile.creators[normal(post.author?.id)] || 0) > 0) return `Mehr von ${post.author.name}`;
  return 'Neu für dich';
}
